/**
 * VendorBroadcastModal - Send a message to the entity channel + push notify favorited users.
 * Rate limited to 5 broadcasts per hour server-side.
 *
 * @atomic @idempotent
 */
import { memo, useState, useCallback } from 'react'
import { XMarkIcon, MegaphoneIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import { vendorBroadcastAPI } from '../../services/api/vendor'

const logger = createLogger('VendorBroadcastModal')
const MAX_CHARS = 280

const VendorBroadcastModal = memo(function VendorBroadcastModal({ entityId, entityName, onClose }) {
  const { t } = useTranslation()
  const { execute, loading } = useAsyncOperation()
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')

  const charsLeft = MAX_CHARS - message.length

  const handleSend = useCallback(async (e) => {
    e.preventDefault()
    const trimmed = message.trim()
    if (!trimmed) return

    await execute(
      () => vendorBroadcastAPI.send(entityId, title.trim(), trimmed),
      {
        onSuccess: () => {
          toast.success(t('vendor.broadcast.sent', 'Broadcast sent!'))
          logger.info('Broadcast sent', { entityId })
          onClose?.()
        },
        onError: (err) => {
          logger.error('Broadcast failed', err)
          const isRateLimited = err?.response?.status === 429
          toast.error(isRateLimited
            ? t('vendor.broadcast.rateLimited', 'Too many broadcasts — try again later')
            : (err?.message || t('common.errorOccurred', 'Something went wrong'))
          )
        }
      }
    )
  }, [entityId, title, message, execute, t, onClose])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <MegaphoneIcon className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-900">
              {t('vendor.broadcast.title', 'Broadcast to followers')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            onTouchEnd={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 pointer-events-none" />
          </button>
        </div>

        <p className="px-4 pb-2 text-xs text-gray-500">
          {entityName}
        </p>

        <form onSubmit={handleSend} className="px-4 pb-4 space-y-3">
          {/* Optional title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            placeholder={t('vendor.broadcast.titlePlaceholder', 'Subject (optional)')}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
          />

          {/* Message body */}
          <div className="relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MAX_CHARS}
              rows={4}
              placeholder={t('vendor.broadcast.placeholder', 'What do you want to tell your followers?')}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className={`absolute bottom-2 right-2 text-xs ${charsLeft < 20 ? 'text-orange-500' : 'text-gray-400'}`}>
              {t('vendor.broadcast.charCount', '{{count}} left', { count: charsLeft })}
            </span>
          </div>

          <button
            type="submit"
            disabled={loading || message.trim().length === 0}
            className="w-full py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? t('app.loading', 'Sending…')
              : t('vendor.broadcast.send', 'Send Broadcast')}
          </button>
        </form>
      </div>
    </div>
  )
})

export default VendorBroadcastModal
